import * as React from "react";
import { Alert, AlertTitle, Card, CardHeader } from "@mui/material";
import { getErrorMsg } from "../../../../utilities/rtkQueryErrorHelpers";
import {
    useDeleteJournalEntryMutation,
    useSearchJournalEntriesMutation,
} from "../../../../services/cclTokenedJournalEntryApi";
import { JournalEntry } from "../../../../services/types/journalEntryTypes";
import ComponentLoader from "../../../../components/common/componentLoader";
import HistoryTimeLine from "../../../../components/common/historyTimeline/historyTimeline";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import {
    UserClaimsService,
    eGroupIds,
} from "../../../../services/currentUserService/currentUserService";
import ResourceNotesDrawer from "../resourceNotesDrawer";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { AccessEventIndividualDetails } from "../../../../services/types/accessEventTypes";

interface ResourceNotesEntryTimelineCardProps {
    pKey: string;
    imKey: string;
    email: string;
    drawerOpen: boolean;
    onDrawerClose: () => void;
}

const ResourceNotesEntryTimelineCard: React.FC<ResourceNotesEntryTimelineCardProps> = (props) => {
    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(props.drawerOpen);
    const [snackbarOpen, setSnackbarOpen] = React.useState<boolean>(false);
    const [allJournalEntries, setAllJournalEntries] = React.useState<JournalEntry[]>([]);

    const { logEvent } = useLogAccessEvent();

    const [
        runSearchJournalEntries,
        {
            isLoading: journalEntriesLoading,
            isError: journalEntriesIsError,
            error: journalEntriesError,
        },
    ] = useSearchJournalEntriesMutation();

    const [runDeleteJournalEntry] = useDeleteJournalEntryMutation();

    const claimsService = new UserClaimsService();
    const allowJournalEntries = claimsService.IsUserInGroups([
        eGroupIds.eClientAdmin,
        eGroupIds.eNWAAdmin,
        eGroupIds.eCCLAdmin,
        eGroupIds.eCCLSuperAdmin,
    ]);

    React.useEffect(() => {
        if (props.drawerOpen === drawerOpen) return;
        setDrawerOpen(props.drawerOpen);
        //eslint-disable-next-line
    }, [props.drawerOpen]);

    const [msg, setMsg] = React.useState<string>("");
    const [severity, setSeverity] = React.useState<"error" | "success">("success");

    const deleteNote = async (journalEntryId: number) => {
        try {
            await runDeleteJournalEntry(journalEntryId).unwrap();
            memoizedSearchJournal(props.pKey);
            setSnackbarOpen(true);
            setSeverity("success");
            setMsg("Resource Note Deleted");
            const evtData: AccessEventIndividualDetails = {
                imKey: props.imKey,
                email: props.email,
            };
            logEvent("ResourceNoteDeleted", evtData);
        } catch (err) {
            setSnackbarOpen(true);
            setSeverity("error");
            setMsg("Resource Note Delete Failed");
        }
    };

    const memoizedSearchJournal = React.useCallback(
        (resourceCode: string) => {
            runSearchJournalEntries({
                journalCategoryIds: [4],
                categoryDataFieldName: "resourceKey",
                categoryDataFieldValue: resourceCode,
            })
                .unwrap()
                .then((journalentries: JournalEntry[]) => {
                    setAllJournalEntries([...journalentries]);
                })
                .catch(() => {
                    setAllJournalEntries([]);
                });
        },
        [runSearchJournalEntries],
    );

    React.useEffect(() => {
        if (props.pKey === "") return;
        memoizedSearchJournal(props.pKey);
    }, [props.pKey, memoizedSearchJournal]);

    function showErrorAlert(msg: string | undefined) {
        return (
            <div>
                <Alert severity="error" sx={{ fontSize: 14 }}>
                    <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                    {msg}
                </Alert>
            </div>
        );
    }

    if (journalEntriesIsError) {
        return showErrorAlert(getErrorMsg(journalEntriesError));
    }

    const onJournalEntrySaved = () => {
        memoizedSearchJournal(props.pKey);
        setDrawerOpen(false);
        props.onDrawerClose();
    };

    return (
        <React.Fragment>
            <CclAlertSnackbar
                open={snackbarOpen}
                severity={severity}
                onClose={() => {
                    setSnackbarOpen(false);
                }}
                message={msg}
            />

            {allowJournalEntries && (
                <ResourceNotesDrawer
                    imKey={props.imKey}
                    pKey={props.pKey}
                    email={props.email}
                    open={drawerOpen}
                    onClose={() => { setDrawerOpen(false); props.onDrawerClose(); }}
                    onSave={onJournalEntrySaved}
                />
            )}
            <Card sx={{ height: 1, width: { xs: 1, lg: 0.8, xl: 0.6 }, m: 2, p: 2 }}>
                {allowJournalEntries ? (
                    <CardHeader
                        action={
                            <CclButton mode={"primary"} onClick={() => setDrawerOpen(true)}>
                                Add Note
                            </CclButton>
                        }
                    />
                ) : null}
                {journalEntriesLoading ? (
                    <ComponentLoader msg="Retrieving Notes..." />
                ) : (
                    <HistoryTimeLine
                        callback={deleteNote}
                        events={[]}
                        journalEntries={allJournalEntries}
                    />
                )}
            </Card>
        </React.Fragment>
    );
};

export default ResourceNotesEntryTimelineCard;
